import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import PaginationBar from './components/PaginationBar';
import TableContainer from './components/TableContainer';
import DataTable from './components/DataTable';
import ResponsiveShadow from './components/ResponsiveShadow';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import ReceitaModal from './components/modals/ReceitaModal';
import ExportarReceitaFndeModal from './components/modals/ExportarReceitaFndeModal';
import TransferirContaModal from './components/modals/TransferirContaModal';
import LoadingModal from './components/modals/LoadingModal';
import usePagination10 from './components/hooks/usePagination10';
import useReferenceData from './components/hooks/useReferenceData';
import { fndeService } from './api/fndeService';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import { formatCurrency } from './utils/formatMoeda';
import { formatDateView, toIsoDate } from './utils/dateUtils';
import Toolbar from './components/Toolbar';

const emptyForm = { ano: '', idPrograma: '', idAcao: '', idEscola: '', idConta: '', destinacao: '', vlrC: '', vlrK: '', data: '' };

function Receitas() {
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const ano = localStorage.getItem('ano');
  const isGestor = JSON.parse(localStorage.getItem('vinculo') || '{}')?.tipo === 'GEST';
  const isMaster = JSON.parse(localStorage.getItem('user') || '{}')?.role === 'MASTER';
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ ...emptyForm, idEscola: cnpj || '', ano: ano || '' });
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [atualizar, setAtualizar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [dadosFnde, setDadosFnde] = useState([]);
  const [showTransferirModal, setShowTransferirModal] = useState(false);
  const [transferirTarget, setTransferirTarget] = useState(null);

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const { setPagination } = usePagination10(items.length);
  const { programas, acoes, contas } = useReferenceData(token, cnpj);
  const displayed = items.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  const resumoPorPrograma = useMemo(() => {
    const map = {};
    (Array.isArray(items) ? items : []).forEach(it => {
      const chave = String(it.idPrograma);
      if (!map[chave]) map[chave] = { idPrograma: it.idPrograma, custeio: 0, capital: 0, qtdC: 0, qtdK: 0 };
      const vlrC = parseFloat(it.vlrC || 0) || 0;
      const vlrK = parseFloat(it.vlrK || 0) || 0;
      map[chave].custeio += vlrC;
      map[chave].capital += vlrK;
      if (vlrC > 0) map[chave].qtdC += 1;
      if (vlrK > 0) map[chave].qtdK += 1;
    });
    return Object.values(map);
  }, [items]);

  useEffect(() => {
    if (!cnpj || !ano) { showDefaultModal('error', 'Escola ou ano não selecionado.'); setIsLoading(false); return; }
    setIsLoading(true);
    axios.get(`${API_BASE_URL.RECEITA}/all`, {
      params: { idEscola: cnpj, ano }, headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => {
        setItems(r.data);
      })
      .catch(err => {
        if (err.response && err.response.status !== 200 && err.response.status !== 404) {
          showDefaultModal('error', 'Erro ao carregar receitas.');
        }
      })
      .finally(() => setIsLoading(false));
  }, [token, cnpj, ano, atualizar]);

  function handleChange(e) { const { name, value } = e.target; setForm(f => ({ ...f, [name]: value })); }
  function handleNew() { setForm({ ...emptyForm, idEscola: cnpj || '', ano: ano || '', _isEditing: false }); setShowModal(true); }
  function handleEdit() { if (selected.length !== 1) return; const s = selected[0]; setForm({ ...s, data: toIsoDate(s.data), _isEditing: true }); setShowModal(true); }

  function handleSave(e) {
    e.preventDefault();
    axios.post(`${API_BASE_URL.RECEITA}/save`, form, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        setShowModal(false);
        setAtualizar(a => !a);
        setSelected([]); // Limpa seleção após salvar
      })
      .catch(() => showDefaultModal('error', 'Erro ao salvar receita.'));
  }

  function handleDelete() {
    if (selected.length === 0) return;
    showConfirmModal('delete', () => Promise.all(selected.map(s => axios.post(`${API_BASE_URL.RECEITA}/delete`, {
      ano: s.ano, idPrograma: s.idPrograma, idAcao: s.idAcao, idEscola: s.idEscola, idConta: s.idConta, destinacao: s.destinacao
    }, { headers: { Authorization: `Bearer ${token}` } })))
      .then(() => { setSelected([]); setAtualizar(a => !a); })
      .catch(() => showDefaultModal('error', 'Erro ao excluir receita.'))
    );
  }

  function handleTransferir() {
    if (selected.length !== 1) return;
    setTransferirTarget(selected[0]);
    setShowTransferirModal(true);
  }

  function handleConfirmTransferir(novaConta) {
    if (!transferirTarget) return;
    axios.post(`${API_BASE_URL.RECEITA}/transferir`, {
      ano: transferirTarget.ano,
      idPrograma: transferirTarget.idPrograma,
      idAcao: transferirTarget.idAcao,
      idEscola: transferirTarget.idEscola,
      idContaOrigem: transferirTarget.idConta,
      novaConta,
      destinacao: transferirTarget.destinacao || '',
      vlrC: transferirTarget.vlrC,
      vlrK: transferirTarget.vlrK,
      data: toIsoDate(transferirTarget.data),
    }, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        setShowTransferirModal(false);
        setTransferirTarget(null);
        setSelected([]);
        setAtualizar(a => !a);
        showDefaultModal('success', `Receita transferida para a conta ${novaConta}.`);
      })
      .catch(err => {
        const msg = (err.response && err.response.data) || 'Erro ao transferir receita.';
        showDefaultModal('error', msg);
      });
  }

  const parseBRL = (v) => {
    if (!v) return null;
    const s = String(v).replace(/\./g, '').replace(',', '.');
    const n = parseFloat(s);
    return isNaN(n) ? null : n;
  };

  async function handleImportarFnde() {
    if (!cnpj || !ano) return;
    setIsImporting(true);
    try {
      // Tentar usar o INEP (co_escola) quando disponível para replicar a consulta feita no navegador
      const escola = JSON.parse(localStorage.getItem('escola') || '{}');
      const co_escola = escola.inep || '';
      const resp = await fndeService.buscarDadosEscola(ano, cnpj, co_escola);
      if (!resp.data.sucesso) { showDefaultModal('error', `Erro FNDE: ${resp.data.erro}`); return; }
      // Resp can contain either 'dados' (lista) or 'secoes' (array de seções)
      let dados = [];
      if (resp.data.dados && Array.isArray(resp.data.dados) && resp.data.dados.length) {
        dados = resp.data.dados;
      } else if (resp.data.secoes && Array.isArray(resp.data.secoes)) {
        // Mesclar dados de TODAS as seções de destinação, pois o FNDE separa a
        // consulta em uma tabela por programa. No layout atual o título do card
        // não menciona destinação (é o programa + dados bancários), então o
        // filtro considera título, colunas e também os próprios registros.
        const temDestinacao = (s) => {
          const titulo = (s.titulo || '').toString().toLowerCase();
          if (titulo.includes('destin')) return true;
          const cols = s.colunas || [];
          if (Array.isArray(cols) && cols.some(c => (c || '').toString().toLowerCase().includes('destin'))) return true;
          const dados = Array.isArray(s.dados) ? s.dados : [];
          return dados.some(d => d && Object.keys(d).some(k => (k || '').toString().toLowerCase().includes('destin')));
        };
        const secoesDestin = resp.data.secoes.filter(temDestinacao);
        const dadosCombinados = secoesDestin.reduce(
          (acc, s) => (Array.isArray(s.dados) ? acc.concat(s.dados) : acc), []
        );
        if (dadosCombinados.length) {
          dados = dadosCombinados;
        } else {
          // como fallback, pegar concat de todas as seções que têm dados
          dados = secoes.reduce((acc, s) => (Array.isArray(s.dados) ? acc.concat(s.dados) : acc), []);
        }
      }

      if (!dados || !dados.length) { showDefaultModal('warning', 'Nenhum dado retornado pelo FNDE.'); return; }
      setDadosFnde(dados);
      setShowExportModal(true);
    } catch (e) {
      showDefaultModal('error', `Erro ao consultar FNDE: ${e.message}`);
    } finally {
      setIsImporting(false);
    }
  }

  async function handleExportarReceita(linhas) {
    setIsLoading(true);
    try {
      await Promise.all(linhas.map(l =>
        axios.post(`${API_BASE_URL.RECEITA}/save`, {
          ano: parseInt(ano), idPrograma: parseInt(l.idPrograma),
          idAcao: parseInt(l.idAcao), idEscola: cnpj, idConta: l.idConta,
          destinacao: l.destinacao || '', vlrC: parseBRL(l.vlrC),
          vlrK: parseBRL(l.vlrK), data: l.data || null,
        }, { headers: { Authorization: `Bearer ${token}` } })
      ));
      setShowExportModal(false);
      setAtualizar(a => !a);
      showDefaultModal('success', `${linhas.length} receita(s) importada(s) com sucesso!`);
    } catch { showDefaultModal('error', 'Erro ao importar receitas.'); }
    finally { setIsLoading(false); }
  }

  function keyOf(item) { return `${item.ano}_${item.idPrograma}_${item.idAcao}_${item.idEscola}_${item.idConta}_${item.destinacao}`; }
  function toggleSelect(item) {
    setSelected(prev => prev.find(s => keyOf(s) === keyOf(item)) ? [] : [item]);
  }

  return (
    <Layout>
      <LoadingModal show={isImporting} />
      <ExportarReceitaFndeModal
        show={showExportModal}
        onClose={() => setShowExportModal(false)}
        onSubmit={handleExportarReceita}
        dados={dadosFnde}
        programas={programas}
        acoes={acoes}
        contas={contas}
      />
      <ResponsiveShadow>
        <PageHeader title="RECEITAS" icon="bi-cash-stack" />
        <PageContent>
          <Toolbar
            onNew={!isGestor ? handleNew : undefined}
            onEdit={!isGestor ? handleEdit : undefined} disableEdit={selected.length !== 1}
            onDelete={!isGestor ? handleDelete : undefined} disableDelete={selected.length === 0}
          >
            {!isGestor && <button className="btn btn-primary btn-sm botao-igual" onClick={handleImportarFnde} disabled={isImporting}><i className="bi bi-database-add me-1"></i>Importar FNDE</button>}
            {isMaster && <button className="btn btn-warning btn-sm botao-igual" onClick={handleTransferir} disabled={selected.length !== 1}><i className="bi bi-arrow-left-right me-1"></i>Transferir</button>}
          </Toolbar>
          {resumoPorPrograma.length > 0 && (
            <div className="mb-2 d-flex flex-wrap gap-2" style={{ maxHeight: '30vh', overflowY: 'auto', overflowX: 'hidden', flexShrink: 0 }}>
              {resumoPorPrograma.map(prog => (
                <div className="card border-0 shadow-sm" key={String(prog.idPrograma)} style={{ borderRadius: 10, flex: '1 1 0', minWidth: 200, maxWidth: '25%' }}>
                  <div className="card-body pt-2 pb-1 px-3">
                    <div className="d-flex justify-content-between align-items-center gap-2" style={{ backgroundColor: '#198754', color: '#fff', margin: '-0.5rem -1rem 0.5rem', padding: '0.15rem 1rem', borderRadius: '10px 10px 0 0', minHeight: '22px' }}>
                      <span className="fw-bold text-truncate" style={{ fontSize: '.62rem' }} title={programas.find(p => p.id === prog.idPrograma)?.descricao || prog.idPrograma}>{programas.find(p => p.id === prog.idPrograma)?.descricao || prog.idPrograma}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span className="text-muted">Custeio</span><span>{formatCurrency(prog.custeio)}</span>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: '.72rem', whiteSpace: 'nowrap' }}>
                      <span className="text-muted">Capital</span><span>{formatCurrency(prog.capital)}</span>
                    </div>
                    <div className="d-flex justify-content-between pt-1 mt-1 border-top fw-bold" style={{ fontSize: '.78rem', whiteSpace: 'nowrap' }}>
                      <span>Total</span><span className="text-success">{formatCurrency(prog.custeio + prog.capital)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <TableContainer isLoading={false} style={{ flex: '1 1 auto', minWidth: 0 }}>
            <DataTable style={{ fontSize: '0.75rem' }} head={
                <tr>
                  <th style={{ width: '3rem', verticalAlign: 'middle', textAlign: 'center' }}>#</th>
                  <th style={{ verticalAlign: 'middle', textAlign: 'center' }}>Data</th>
                  <th style={{ verticalAlign: 'middle', textAlign: 'center' }}>Programa / Ação</th>
                  <th style={{ verticalAlign: 'middle', textAlign: 'center' }}>Conta</th>
                  <th style={{ verticalAlign: 'middle', textAlign: 'center' }}>Destinação</th>
                  <th style={{ verticalAlign: 'middle', textAlign: 'center' }}>Valor Custeio</th>
                  <th style={{ verticalAlign: 'middle', textAlign: 'center' }}>Valor Capital</th>
                  <th style={{ verticalAlign: 'middle', textAlign: 'center' }}>Total</th>
                </tr>
              }>
              <tbody>
                {displayed.map((item, idx) => {
                  const nomeProgramaAcao = `${item.programaDescricao || item.idPrograma} / ${item.acaoDescricao || item.idAcao}`;
                  const total = (parseFloat(item.vlrC || 0) + parseFloat(item.vlrK || 0));
                  return (
                    <tr key={keyOf(item)} className={selected.find(s => keyOf(s) === keyOf(item)) ? 'table-success' : ''} onClick={() => toggleSelect(item)} style={{ cursor: 'pointer' }}>
                      <td className="text-center align-middle">{setPagination.startIndex + idx + 1}</td>
                      <td className="align-middle text-center">{formatDateView(item.data)}</td>
                      <td className="align-middle">{nomeProgramaAcao}</td>
                      <td className="align-middle">{item.idConta}</td>
                      <td className="align-middle">{item.destinacao}</td>
                      <td className="align-middle text-end">{formatCurrency(item.vlrC)}</td>
                      <td className="align-middle text-end">{formatCurrency(item.vlrK)}</td>
                      <td className="align-middle text-end fw-bold text-success" style={{ fontSize: '0.75rem' }}>{formatCurrency(total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </DataTable>
          </TableContainer>
        </PageContent>
        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>
      <ReceitaModal show={showModal} onClose={() => setShowModal(false)} onSubmit={handleSave} form={form} handleChange={handleChange} programas={programas} acoes={acoes} contas={contas} />
      <TransferirContaModal
        show={showTransferirModal}
        onClose={() => setShowTransferirModal(false)}
        onConfirm={handleConfirmTransferir}
        origem={transferirTarget ? transferirTarget.idConta : ''}
        idPrograma={transferirTarget ? transferirTarget.idPrograma : null}
      />
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
    </Layout>
  );
}

export default Receitas;
